import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle2, ListChecks, FileText, MessageSquare, Folder, Database, Presentation, Network, Globe, History,
} from 'lucide-react';
import { cn } from '../../lib/utils';

/** Every document type a project can hold, in the order people reach for them. */
export const DOC_TYPES = [
  { type: 'folder', icon: Folder },
  { type: 'database', icon: Database },
  { type: 'whiteboard', icon: Presentation },
  { type: 'mindmap', icon: Network },
  { type: 'embed', icon: Globe },
  { type: 'changelog', icon: History },
];

/**
 * One "New": a task, a list, a document or a chat as tiles (outside a project a
 * project too), the other document types as chips below. The project's "+" in the
 * sidebar, the project header, the phone's sheet and the app's New (Ctrl+K, the
 * sidebar's "+ New", the phone's "+": components/create/NewPanel.jsx) all show this,
 * so creating anything works the same everywhere. Only the actions passed in are
 * offered (a folder holds documents only). The tiles' letters work as shortcuts
 * while it is open. variant: popover (its own card), sheet (phones) or panel (inside
 * New, under the one input).
 */
const CreateMenu = ({ project, onTask, onList, onDocument, onChat, onProject, docTypes = true, variant = 'popover', className }) => {
  const { t } = useTranslation();
  const sheet = variant === 'sheet';
  const popover = variant === 'popover';
  const tiles = [
    onTask && { key: 't', label: t('nav.create.task'), icon: CheckCircle2, run: onTask, testId: 'create-task' },
    onList && { key: 'l', label: t('projects.layout_list'), icon: ListChecks, run: onList, testId: 'create-list' },
    onDocument && { key: 'd', label: t('projects.doc_types.document'), icon: FileText, run: () => onDocument('document'), testId: 'create-document' },
    onChat && { key: 'c', label: t('nav.create.chat'), icon: MessageSquare, run: onChat, testId: 'create-chat' },
    onProject && { key: 'p', label: t('common.project'), icon: Folder, run: onProject, testId: 'create-project' },
  ].filter(Boolean);

  useEffect(() => {
    // Letters are shortcuts only where there is a keyboard to press them: on a phone the
    // first letters typed before tapping into the input would open a tile instead.
    if (sheet) return undefined;
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target;
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
      const tile = tiles.find((x) => x.key === e.key.toLowerCase());
      if (tile) { e.preventDefault(); tile.run(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return (
    <div role="menu" aria-label={project ? t('nav.create.new_in', { name: project.name }) : t('nav.create.new')}
      className={cn('flex flex-col gap-3', popover && 'w-[380px] max-w-[calc(100vw-16px)] p-3 rounded-2xl border border-border bg-popover shadow-xl', className)}>
      {project && popover && (
        <div className="flex items-center gap-2 px-0.5 text-[12.5px] text-muted-foreground min-w-0">
          <span className="flex-shrink-0">{t('nav.create.new')}</span>
          <span className="w-2 h-2 rounded-[3px] flex-shrink-0" style={{ background: project.color || '#3B82F6' }} />
          <span className="truncate font-medium text-foreground">{project.name}</span>
        </div>
      )}
      {/* The design's tiles (MobileNew): the icon at the top left, the name under it; two a row
          on phones, all in one row elsewhere. */}
      <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${sheet ? 2 : Math.max(tiles.length, 1)}, minmax(0, 1fr))` }}>
        {tiles.map(({ key, label, icon: Icon, run, testId }) => (
          <button key={key} type="button" role="menuitem" onClick={run} data-testid={testId}
            className={cn('relative flex flex-col items-start justify-between gap-2 rounded-xl border border-border bg-card p-3 text-left hover:bg-accent transition-colors',
              sheet ? 'h-[76px]' : 'h-[72px]')}>
            <Icon className="w-5 h-5 text-brand-icon" />
            <span className="max-w-full truncate text-[13.5px] font-medium text-foreground">{label}</span>
            {!sheet && <kbd className="absolute top-2 right-2.5 font-mono text-[10.5px] text-muted-foreground/70 uppercase">{key}</kbd>}
          </button>
        ))}
      </div>
      {onDocument && docTypes && (
        <div className="flex flex-wrap gap-1.5">
          {DOC_TYPES.map(({ type, icon: Icon }) => (
            <button key={type} type="button" role="menuitem" onClick={() => onDocument(type)} data-testid={`create-doc-${type}`}
              className={cn('flex items-center gap-1.5 pl-2.5 pr-3 rounded-full border border-border text-[12.5px] text-foreground hover:bg-accent transition-colors',
                sheet ? 'h-10' : 'h-8')}>
              <Icon className="w-3.5 h-3.5 text-brand-icon" />
              {t(`projects.doc_types.${type}`)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CreateMenu;
